import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { object, string, type StringSchema } from 'yup';
import { useFormik } from 'formik';
import styled from 'styled-components';
import { PriorityHigh } from '@mui/icons-material';
import { Box, Grid } from '@mui/material';
import { editPublicationPermission } from './editPublicationPermission';
import {
  PublicationTypesOptions,
  isNotPaper,
  ApplicantRoleOptions,
  PublicationStatusOptions,
} from './options';
import {
  type PublicationFormValues,
  ApplicantPublicationRole,
  type PublicationBoxData,
  PublicationStatus,
  PublicationTypes,
} from './types';
import { PAGE_TEXT } from '../lang';
import {
  type PublicationPayload,
  updatePublication,
  addApplicationItem,
  checkInvalidDate,
} from '../../../graphql';
import {
  type AlertDetails,
  Alert,
  SwitchButton,
  TextField,
  Select,
  Button,
  DatePicker,
  IconButton,
  FilesPanel,
  FILE_MANAGE_ORDER,
} from '../../../../../components';
import {
  handleReadySubmit,
  handleFileRename,
  type FormController,
} from '../../../../../utils';

export type PublicationFormProps = {
  applicationNo: string;
  initialValues?: PublicationPayload;
  index: number;
  enableEdit: FormController;
  enableDelete: boolean;
  handleRemovePublication: () => void;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

const pageText = PAGE_TEXT.OTHER_INFORMATION.PUBLICATION;
const publicationBoxData: PublicationBoxData = {
  type: pageText.FORM_LABELS.TYPE,
  other_type: pageText.FORM_LABELS.OTHER_TYPE,
  name: pageText.FORM_LABELS.NAME,
  title: pageText.FORM_LABELS.TITLE,
  author: pageText.FORM_LABELS.AUTHOR,
  role: pageText.FORM_LABELS.ROLE,
  role_other: pageText.FORM_LABELS.ROLE_OTHER,
  status: pageText.FORM_LABELS.STATUS,
  date: pageText.FORM_LABELS.DATE,
  issue: pageText.FORM_LABELS.ISSUE,
  reviewed: pageText.FORM_LABELS.REVIEWED,
  citation: pageText.FORM_LABELS.CITATION,
  url: pageText.FORM_LABELS.URL,
};

const handleSearchCitation = (): void => {
  window.open(
    'https://www.polyu.edu.hk/en/elc/independent-learning/language-resources/referencing',
    '_blank',
  );
};

const ExclaimIconLabel = (
  <span>
    {publicationBoxData.citation}
    <span title="Please refer to the citation format.">
      <IconButton
        onClick={handleSearchCitation}
        icon={
          <PriorityHigh
            sx={{
              color: '#FFFFFF',
              backgroundColor: '#951D1D',
              '&:hover': { backgroundColor: '#951D1D' },
              borderRadius: '50%',
              width: '0.5em',
              height: '0.5em',
            }}
          />
        }
      />
    </span>
  </span>
);

export const PublicationForm: React.FunctionComponent<PublicationFormProps> = (
  props: PublicationFormProps,
) => {
  const {
    applicationNo,
    initialValues = {
      publicationId: '',
      publicationType: '',
      otherType: '',
      publicationAuthors: '',
      publicationTitle: '',
      publicationName: '',
      applicantRole: '',
      specificRole: '',
      publicationStatus: '',
      publicationDate: null,
      peerReviewedIndicator: false,
      issuePageArticelNo: '',
      citationInfo: '',
      publicationUrl: '',
      supportingDocuments: [],
    },
    enableEdit,
    enableDelete,
    index: publicationIndex,
    handleRemovePublication,
    refetchApplication,
    handleLoading,
  } = props;

  const {
    publicationId: initialPublicationId,
    supportingDocuments,
    ...others
  } = initialValues;

  const { applicationId } = useParams();

  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'error',
  };
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);
  const [publicationId, setPublicationId] = useState(initialPublicationId);
  const [files, setFiles] = useState(supportingDocuments);

  const hasPublication = Boolean(publicationId);

  const isPublicationTypeRequired = (
    publicationType: string,
    schema: StringSchema,
  ): any => {
    const required: boolean = !isNotPaper.includes(publicationType ?? '');
    return required ? schema.required('Required') : schema.notRequired();
  };

  const isStatusPublished = (
    publicationStatus: string,
    schema: StringSchema,
  ): any => {
    const required: boolean = publicationStatus === PublicationStatus.PUBLISHED;
    return required ? schema.required('Required') : schema.notRequired();
  };

  const handleChangePublicationType = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const { value } = event.target;
    await formik.setValues({
      ...formik.values,
      publicationStatus: '',
      publicationDate: null,
      peerReviewedIndicator: false,
      issuePageArticelNo: '',
      citationInfo: '',
      publicationUrl: '',
      publicationType: value,
      otherType: '',
      specificRole: '',
      applicantRole: '',
    });
    formik.handleChange(event);
  };

  const handleChangeApplicantRole = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setFieldValue('specificRole', '');
    formik.handleChange(event);
  };

  const handleFileChange = (order: FILE_MANAGE_ORDER, id: string): boolean => {
    let newSupportingDocuments = [...files];
    switch (order) {
      case FILE_MANAGE_ORDER.REMOVE:
        // suppose document id is unique
        newSupportingDocuments = newSupportingDocuments.filter(file => {
          return file.id !== id;
        });
        break;
      case FILE_MANAGE_ORDER.UPLOAD:
        if (id) {
          newSupportingDocuments = [
            ...newSupportingDocuments,
            {
              id,
              name: '',
              link: '',
            },
          ];
        }
        break;
      default:
    }
    setFiles(newSupportingDocuments);
    return true;
  };

  const createPublicationGql = addApplicationItem(
    'publications',
    refetchApplication,
  );
  const updatePublicationGql = updatePublication();
  const handleSubmit = async (values: PublicationFormValues): Promise<void> => {
    const {
      publicationDate,
      publicationType,
      publicationStatus,
      applicantRole,
      otherType,
      ...others
    } = values;
    const input = {
      ...others,
      publicationType:
        publicationType === PublicationTypes.OTHER_PUBLICATION
          ? otherType
          : publicationType,
      // if publicationDate is empty, set it to null
      publicationDate: checkInvalidDate(publicationDate)
        ? null
        : publicationDate,
      // publicationStatus must be one of the PublicationStatus enum
      publicationStatus: Object.values(PublicationStatus).includes(
        publicationStatus as any,
      )
        ? publicationStatus
        : null,
      // applicantRole must be one of the ApplicantPublicationRole enum
      applicantRole: Object.values(ApplicantPublicationRole).includes(
        applicantRole as any,
      )
        ? applicantRole
        : null,
      supportingDocuments: files.map((file, index) => {
        const { name, ...other } = file;
        return {
          name: handleFileRename(applicationNo, 'publication', index + 1),
          ...other,
        };
      }),
    };

    await handleLoading(async () => {
      try {
        if (!hasPublication) {
          const res = await createPublicationGql({
            variables: {
              id: applicationId,
              input: {
                publication: {
                  ...input,
                },
              },
            },
          });
          const resPublicationId =
            res.data?.addApplicationItem?.publications?.[publicationIndex]?._id;
          setPublicationId(resPublicationId);
        } else {
          await updatePublicationGql({
            variables: {
              id: publicationId,
              ...input,
            },
          });
        }
        alertDetailDef = {
          alertContent: 'Saved Publication Successfully.',
          alertType: 'success',
        };
        refetchApplication();
      } catch (error: any) {
        alertDetailDef = {
          alertContent: `Saved Publication Failed.${(error as Error)?.message}`,
          alertType: 'error',
        };
      } finally {
        setAlertDetails(alertDetailDef);
      }
    });
    setOpenAlert(true);
  };

  const formik = useFormik({
    initialValues: {
      ...others,
    },
    validationSchema: object({
      publicationType: string().required('Required'),
      otherType: string().when(
        'publicationType',
        ([publicationType], schema) => {
          const required: boolean = publicationType === 'OTHER_PUBLICATION';
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),
      publicationName: string().when(
        'publicationType',
        ([publicationType], schema) =>
          isPublicationTypeRequired(publicationType, schema),
      ),
      publicationTitle: string().required('Required'),
      publicationAuthors: string().required('Required'),
      applicantRole: string().when(
        'publicationType',
        ([publicationType], schema) =>
          isPublicationTypeRequired(publicationType, schema),
      ),
      specificRole: string().when(
        ['applicantRole', 'publicationType'],
        ([applicantRole, publicationType], schema) => {
          const required: boolean =
            applicantRole === ApplicantPublicationRole.OTHER ||
            isNotPaper.includes(publicationType ?? '');
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),
      publicationStatus: string().when(
        'publicationType',
        ([publicationType], schema) =>
          isPublicationTypeRequired(publicationType, schema),
      ),
      publicationDate: string().when(
        'publicationStatus',
        ([publicationStatus], schema) =>
          isStatusPublished(publicationStatus, schema),
      ),
      issuePageArticelNo: string().when(
        'publicationStatus',
        ([publicationStatus], schema) =>
          isStatusPublished(publicationStatus, schema),
      ),
      citationInfo: string().when(
        'publicationStatus',
        ([publicationStatus], schema) =>
          isStatusPublished(publicationStatus, schema),
      ),
      publicationUrl: string().when(
        'publicationStatus',
        ([publicationStatus], schema) =>
          isStatusPublished(publicationStatus, schema),
      ),
    }),
    onSubmit: handleSubmit,
  });

  const publicationPermission = editPublicationPermission(
    initialValues,
    {
      ...formik.values,
      supportingDocuments: files,
    },
    enableEdit,
  );
  const isPublished =
    formik.values.publicationStatus === PublicationStatus.PUBLISHED;

  const isNotAPaper = isNotPaper.includes(formik.values.publicationType ?? '');
  const isOtherType = formik.values.publicationType === 'OTHER_PUBLICATION';

  const handleChangePublicationStatus = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setValues({
      ...formik.values,
      peerReviewedIndicator: false,
      publicationDate: null,
      issuePageArticelNo: '',
      citationInfo: '',
      publicationUrl: '',
    });
    formik.handleChange(event);
  };

  const handlePeerReviewChange = async (
    event: React.SyntheticEvent<Element, Event>,
    checked: boolean,
  ): Promise<void> => {
    await formik.setFieldValue('peerReviewedIndicator', checked);
    formik.handleChange(event);
  };

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Container>
        <form onSubmit={handleReadySubmit(formik, publicationIndex)} noValidate>
          <Grid container columnSpacing={4} rowSpacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <Select
                className="w-full"
                label={publicationBoxData.type}
                disabled={!publicationPermission.publicationType}
                options={PublicationTypesOptions}
                required
                InputLabelProps={{ shrink: true }}
                value={formik.values.publicationType}
                name="publicationType"
                onChange={handleChangePublicationType}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.publicationType &&
                  Boolean(formik.errors.publicationType)
                }
                helperText={
                  formik.touched.publicationType &&
                  formik.errors.publicationType
                }
              />
            </Grid>
            {isOtherType && (
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <TextField
                  className="w-full"
                  required
                  disabled={!publicationPermission.otherType}
                  label={publicationBoxData.other_type}
                  inputProps={{ maxLength: 250 }}
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.otherType}
                  name="otherType"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.otherType && Boolean(formik.errors.otherType)
                  }
                  helperText={
                    formik.touched.otherType && formik.errors.otherType
                  }
                />
              </Grid>
            )}
            {!isNotAPaper && (
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <TextField
                  className="w-full"
                  required
                  disabled={!publicationPermission.publicationName}
                  label={publicationBoxData.name}
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.publicationName}
                  inputProps={{ maxLength: 250 }}
                  name="publicationName"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.publicationName &&
                    Boolean(formik.errors.publicationName)
                  }
                  helperText={
                    formik.touched.publicationName &&
                    formik.errors.publicationName
                  }
                />
              </Grid>
            )}
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <TextField
                className="w-full"
                required
                disabled={!publicationPermission.publicationTitle}
                label={publicationBoxData.title}
                InputLabelProps={{ shrink: true }}
                value={formik.values.publicationTitle}
                name="publicationTitle"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: 250 }}
                error={
                  formik.touched.publicationTitle &&
                  Boolean(formik.errors.publicationTitle)
                }
                helperText={
                  formik.touched.publicationTitle &&
                  formik.errors.publicationTitle
                }
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <TextField
                className="w-full"
                required
                disabled={!publicationPermission.publicationAuthors}
                label={publicationBoxData.author}
                InputLabelProps={{ shrink: true }}
                value={formik.values.publicationAuthors}
                onChange={formik.handleChange}
                name="publicationAuthors"
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: 250 }}
                error={
                  formik.touched.publicationAuthors &&
                  Boolean(formik.errors.publicationAuthors)
                }
                helperText={
                  formik.touched.publicationAuthors &&
                  formik.errors.publicationAuthors
                }
              />
            </Grid>
            {!isNotAPaper && (
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Select
                  className="w-full"
                  required
                  disabled={!publicationPermission.applicantRole}
                  label={publicationBoxData.role}
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.applicantRole}
                  onChange={handleChangeApplicantRole}
                  name="applicantRole"
                  onBlur={formik.handleBlur}
                  options={ApplicantRoleOptions}
                  error={
                    formik.touched.applicantRole &&
                    Boolean(formik.errors.applicantRole)
                  }
                  helperText={
                    formik.touched.applicantRole && formik.errors.applicantRole
                  }
                />
              </Grid>
            )}
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              {(formik.values.applicantRole ===
                ApplicantPublicationRole.OTHER ||
                isNotAPaper) && (
                <TextField
                  className="w-full"
                  required
                  label={publicationBoxData.role_other}
                  value={formik.values.specificRole}
                  InputLabelProps={{ shrink: true }}
                  disabled={!publicationPermission.specificRole}
                  onChange={formik.handleChange}
                  name="specificRole"
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.specificRole &&
                    Boolean(formik.errors.specificRole)
                  }
                  helperText={
                    formik.touched.specificRole && formik.errors.specificRole
                  }
                />
              )}
            </Grid>

            {!isNotAPaper && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Select
                    className="w-full"
                    required
                    disabled={!publicationPermission.publicationStatus}
                    label={publicationBoxData.status}
                    value={formik.values.publicationStatus}
                    InputLabelProps={{ shrink: true }}
                    onChange={handleChangePublicationStatus}
                    name="publicationStatus"
                    onBlur={formik.handleBlur}
                    options={PublicationStatusOptions}
                    error={
                      formik.touched.publicationStatus &&
                      Boolean(formik.errors.publicationStatus)
                    }
                    helperText={
                      formik.touched.publicationStatus &&
                      formik.errors.publicationStatus
                    }
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  {isPublished && (
                    <DatePicker
                      className="w-full"
                      required
                      label={publicationBoxData.date}
                      InputLabelProps={{ shrink: true }}
                      maxDate={dayjs()}
                      value={
                        formik.values.publicationDate !== null
                          ? dayjs(formik.values.publicationDate)
                          : null
                      }
                      name="publicationDate"
                      onChange={date => {
                        void formik.setFieldValue('publicationDate', date);
                      }}
                      onBlur={formik.handleBlur}
                      disabled={!publicationPermission.publicationDate}
                      error={
                        formik.touched.publicationDate &&
                        Boolean(formik.errors.publicationDate)
                      }
                      helperText={
                        formik.touched.publicationDate &&
                        formik.errors.publicationDate
                      }
                    />
                  )}
                </Grid>

                {isPublished && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField
                      className="w-full"
                      required
                      disabled={!publicationPermission.issuePageArticelNo}
                      label={publicationBoxData.issue}
                      InputLabelProps={{ shrink: true }}
                      value={formik.values.issuePageArticelNo}
                      name="issuePageArticelNo"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.issuePageArticelNo &&
                        Boolean(formik.errors.issuePageArticelNo)
                      }
                      helperText={
                        formik.touched.issuePageArticelNo &&
                        formik.errors.issuePageArticelNo
                      }
                    />
                  </Grid>
                )}

                {isPublished && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <span>
                      <SwitchButton
                        disabled={!publicationPermission.peerReviewedIndicator}
                        value={formik.values.peerReviewedIndicator}
                        checked={formik.values.peerReviewedIndicator}
                        onChange={handlePeerReviewChange}
                      />
                      {publicationBoxData.reviewed}
                    </span>
                  </Grid>
                )}

                {isPublished && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField
                      className="w-full"
                      required
                      disabled={!publicationPermission.citationInfo}
                      inputProps={{ maxLength: 500 }}
                      label={ExclaimIconLabel}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      minRows={4}
                      value={formik.values.citationInfo}
                      name="citationInfo"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.citationInfo &&
                        Boolean(formik.errors.citationInfo)
                      }
                      helperText={
                        formik.touched.citationInfo &&
                        formik.errors.citationInfo
                      }
                    />
                  </Grid>
                )}
                {isPublished && (
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <TextField
                      className="w-full"
                      disabled={!publicationPermission.publicationUrl}
                      label={publicationBoxData.url}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 500 }}
                      multiline
                      required
                      minRows={4}
                      value={formik.values.publicationUrl}
                      name="publicationUrl"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.publicationUrl &&
                        Boolean(formik.errors.publicationUrl)
                      }
                      helperText={
                        formik.touched.publicationUrl &&
                        formik.errors.publicationUrl
                      }
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>

          <Box display={'flex'} justifyContent={'center'} marginTop={2}>
            {publicationPermission.enableSave && (
              <Button
                type="submit"
                variant="contained"
                style={{ marginRight: '15px' }}
              >
                {PAGE_TEXT.SAVE}
              </Button>
            )}
            {publicationPermission.enableDelete && enableDelete && (
              <Button variant="outlined" onClick={handleRemovePublication}>
                {PAGE_TEXT.DELETE}
              </Button>
            )}
          </Box>
        </form>
        <div style={{ paddingTop: '15px', marginLeft: '1rem' }}>
          <FilesPanel
            disable={!publicationPermission.supportingDocuments}
            disableDeleteOnly={!enableDelete}
            title={pageText.FILES_PANEL.PANEL_TITLE}
            caption={pageText.FILES_PANEL.PANEL_CAPTION}
            files={files}
            handleFilesChange={handleFileChange}
            handleLoading={handleLoading}
          />
        </div>
      </Container>
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 35px;
  margin-bottom: 2rem;

  .MuiSwitch-root {
    margin: 1rem;
  }

  .MuiSwitch-root {
    margin: 1rem;
  }

  form {
    margin-bottom: 2rem;
    width: 100%;
    padding-top: 0.5rem;
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-top: 1rem;
    button {
      margin: 0 1rem;
    }
  }

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;

    width: calc(100%);
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }
`;
